import { cn } from '@lib/cn';
import { Button } from '@shared/ui';

import type { CellTone, SummaryCell } from '../model/report-overview';

const VALUE_TONE: Readonly<Record<CellTone, string>> = {
    default: 'text-content',
    success: 'text-success-icon',
    warning: 'text-warning-text',
    danger: 'text-danger-text',
};

/** Literal classes, so Tailwind sees them: five cells, or six with records in progress. */
const COLUMNS: Readonly<Record<number, string>> = {
    5: 'md:grid-cols-5 print:grid-cols-5',
    6: 'md:grid-cols-6 print:grid-cols-6',
};

/**
 * ONE bordered bar split into equal cells by hairlines — the numbers a reader
 * takes away. Two columns on a narrow screen; one row on paper.
 */
export function ReportSummaryStrip({ cells }: { cells: readonly SummaryCell[] }) {
    return (
        <section
            aria-label="Summary"
            className={cn(
                'grid grid-cols-2 overflow-hidden rounded-xl border border-line bg-surface',
                COLUMNS[cells.length] ?? COLUMNS[5],
            )}
        >
            {cells.map((cell) => (
                <div
                    key={cell.key}
                    className="-ml-px -mt-px grid gap-0.5 border-l border-t border-line px-5 py-[18px] print:px-3.5"
                >
                    <span
                        className={cn(
                            'text-[30px] font-semibold tabular-nums leading-tight',
                            VALUE_TONE[cell.tone],
                        )}
                    >
                        {cell.value.toLocaleString('en-US')}
                        {cell.total !== null && (
                            <small className="text-[18px] font-medium text-content-muted">
                                /{cell.total.toLocaleString('en-US')}
                            </small>
                        )}
                        {cell.aside !== null && (
                            <small className="ml-1.5 whitespace-nowrap text-[13px] font-medium text-content-muted">
                                {cell.aside}
                            </small>
                        )}
                    </span>
                    <span className="text-[13px] text-content-muted">{cell.label}</span>
                </div>
            ))}
        </section>
    );
}

/** Shown while the report is partial: who is still running, and the way to finish it. */
export function PartialBanner({
    unsettled,
    refreshing,
    canRefresh,
    onRefresh,
}: {
    unsettled: number;
    refreshing: boolean;
    canRefresh: boolean;
    onRefresh: () => void;
}) {
    return (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-warning-line bg-warning-surface px-4 py-3 text-[13.5px] text-warning-text">
            <span className="font-semibold">
                {unsettled} {unsettled === 1 ? 'client has' : 'clients have'} no settled status yet
                — refresh to complete the report.
            </span>
            {refreshing && <span>A refresh is in progress.</span>}
            {canRefresh && (
                <Button
                    size="sm"
                    className="ml-auto print:hidden"
                    onClick={onRefresh}
                    disabled={refreshing}
                >
                    {refreshing ? 'Refreshing…' : 'Refresh'}
                </Button>
            )}
        </div>
    );
}
